import { createElement, useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as PipelineIcon } from '@elementary/common-ui/assets/16x16/pipeline.svg';
import { ReactComponent as TableIcon } from '@elementary/common-ui/assets/16x16/table.svg';
import { ReactComponent as UserIcon } from '@elementary/common-ui/assets/16x16/user.svg';
import { ReactComponent as ColumnIcon } from '@elementary/common-ui/assets/columns.svg';
import { ReactComponent as ArrowDownIcon } from '@elementary/common-ui/assets/arrow-down-expand.svg';
import {
  Avatar,
  DropDown,
  Flex,
  IconButton,
  Menu,
  OverflowTooltip,
  SvgIcon,
} from '@elementary/common-ui/components';
import { getTestIcon } from '@elementary/common-ui/components/cellRenderers/testNameRenderer';
import { Option } from '@elementary/common-ui/components/Menu/Menu';
import { Column, ColumnType } from '@elementary/common-ui/components/Table';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { Tooltip, Typography } from '@mui/material';
import { FetchTeamUsersApi, SourceType } from 'shared/types';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import { FetchIncidentsApi, Incident, IncidentDetails } from 'incidents/types';
import {
  getIncidentDisplayLabel,
  severityIconMap,
  smallStatusIconMap,
} from 'incidents/utils';
import IncidentActionsColumnMenu from '../components/incidentTickets/IncidentActionsColumnMenu';

const Label = styled(Typography)`
  line-height: normal;
  white-space: nowrap;
`;

const ModelName = styled(Flex)`
  overflow: hidden;
  align-items: center;
  border-radius: 8px;
  gap: 4px;
  min-width: 50px;
`;

const TableIconStyled = styled(TableIcon)`
  width: 17px;
  min-width: 17px;
`;

const ColumnIconStyled = styled(ColumnIcon)`
  width: 17px;
  min-width: 17px;
`;

const PipelineIconStyled = styled(PipelineIcon)`
  width: 17px;
  min-width: 17px;
`;

const TestName = styled(Flex)`
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-shrink: 1;

  > svg {
    flex-shrink: 0;
    path {
      fill: ${({ theme }) => theme.palette.common.gray4};
    }
  }
`;

const IconButtonStyled = styled(IconButton)`
  padding: 6px;
`;

type UseIncidentColumnsOptions = {
  severityOptions?: Option[];
  statusOptions?: Option[];
  assigneeOptions?: Option[];
  usersData?: FetchTeamUsersApi;
  disabled?: boolean;
  isAiIncidentsEnabled?: boolean;
  incidentFilters?: FetchIncidentsApi;
  onChange: (id: string, val: Partial<IncidentDetails>) => void;
  onOpenAIChat: (
    id: string,
    startMessage: string,
    displayLabel: string,
  ) => void;
};
const useIncidentColumns = ({
  severityOptions,
  statusOptions,
  assigneeOptions,
  usersData,
  disabled,
  incidentFilters,
  isAiIncidentsEnabled,
  onChange,
  onOpenAIChat,
}: UseIncidentColumnsOptions): Column<Incident>[] =>
  useMemo<Column<Incident>[]>(
    () => [
      {
        id: 'severity',
        header: 'Severity',
        width: 120,
        sortable: false,
        filterable: true,
        isFilterCounterHidden: true,
        filterOptionsOverride: incidentFilters?.options
          ?.find((f) => f.property === 'severity')
          ?.values.map((v) => ({
            label: v,
            value: v,
          })),
        renderer: (incident) => {
          const isDisabled = disabled || incident.status === 'Resolved';
          return (
            <Menu
              tooltip="Set severity"
              menuButton={
                <IconButtonStyled disabled={isDisabled}>
                  <Flex sx={{ alignItems: 'center', gap: 1 }}>
                    {severityIconMap[incident.severity]}
                    <SvgIcon width={10} strokeColor="gray4" fillColor="gray4">
                      <ArrowDownIcon />
                    </SvgIcon>
                  </Flex>
                </IconButtonStyled>
              }
              menuTitle="SET SEVERITY"
              options={severityOptions}
              closeOnChange
              onChange={(val) =>
                onChange(incident.id, {
                  severity: val as IncidentDetails['severity'],
                })
              }
            />
          );
        },
      },
      {
        id: 'test_name',
        header: 'Name',
        width: 300,
        sortable: false,
        filterable: true,
        isFilterCounterHidden: true,
        filterOptionsOverride: incidentFilters?.options
          ?.find((f) => f.property === 'test_name')
          ?.values.map((v) => ({
            label: v,
            value: v,
          })),
        showValueWhenEmpty: true,
        renderer: (incident) => {
          const testName = getIncidentDisplayLabel(
            incident.additional_data.display_name,
            incident.additional_data.sub_type,
          );

          return (
            <TestName>
              {createElement(
                getTestIcon(
                  incident.additional_data.type || '',
                  incident.additional_data.sub_type || '',
                ) || (() => null),
              )}
              <OverflowTooltip>{testName}</OverflowTooltip>
            </TestName>
          );
        },
      },
      {
        id: 'model',
        header: 'Asset',
        width: 300,
        sortable: false,
        filterable: true,
        isFilterCounterHidden: true,
        filterOptionsOverride: incidentFilters?.options
          ?.find((f) => f.property === 'model')
          ?.values.map((v) => ({
            label: v,
            value: v,
          })),
        showValueWhenEmpty: true,
        renderer: (incident) => (
          <ModelName>
            {incident.source_type === SourceType.PIPELINE ? (
              <PipelineIconStyled />
            ) : incident.additional_data.column ? (
              <ColumnIconStyled />
            ) : (
              <TableIconStyled />
            )}
            <OverflowTooltip
              typographySx={{
                lineHeight: 'normal',
                direction: 'rtl',
              }}
            >
              {incident.fully_qualified_name || 'N/A'}
            </OverflowTooltip>
          </ModelName>
        ),
      },
      {
        id: 'database_name',
        header: 'Database',
        width: 150,
        sortable: false,
        filterable: true,
        isFilterCounterHidden: true,
        filterOptionsOverride: incidentFilters?.options
          ?.find((f) => f.property === 'database_name')
          ?.values.map((v) => ({
            label: v,
            value: v,
          })),
      },
      {
        id: 'schema_name',
        header: 'Schema',
        width: 150,
        sortable: false,
        filterable: true,
        isFilterCounterHidden: true,
        filterOptionsOverride: incidentFilters?.options
          ?.find((f) => f.property === 'schema_name')
          ?.values.map((v) => ({
            label: v,
            value: v,
          })),
      },
      {
        id: 'table_name',
        header: 'Table',
        width: 150,
        sortable: false,
        filterable: true,
        isFilterCounterHidden: true,
        filterOptionsOverride: incidentFilters?.options
          ?.find((f) => f.property === 'table_name')
          ?.values.map((v) => ({
            label: v,
            value: v,
          })),
      },
      {
        id: 'column_name',
        header: 'Column',
        width: 150,
        sortable: false,
        filterable: true,
        isFilterCounterHidden: true,
        filterOptionsOverride: incidentFilters?.options
          ?.find((f) => f.property === 'column_name')
          ?.values.map((v) => ({
            label: v,
            value: v,
          })),
      },
      {
        id: 'start_time',
        header: 'First Event',
        showValueWhenEmpty: true,
        sortable: false,
        filterable: false,
        width: 160,
        type: ColumnType.Date,
        renderer: (incident) => (
          <Label>{localizeDate(incident.start_time).fromNow()}</Label>
        ),
      },
      {
        id: 'last_fail',
        header: 'Last Event',
        showValueWhenEmpty: true,
        sortable: false,
        filterable: false,
        width: 160,
        type: ColumnType.Date,
        renderer: (incident) => (
          <Label>{localizeDate(incident.last_fail).fromNow()}</Label>
        ),
      },
      {
        id: 'assignee',
        header: 'Assignee',
        width: 120,
        sortable: false,
        filterable: true,
        isFilterCounterHidden: true,
        filterOptionsOverride: [
          { value: NO_ASSIGNEE_VALUE, label: 'No Assignee' },
          ...(incidentFilters?.options
            ?.find((f) => f.property === 'assignee')
            ?.values.map((v) => ({
              label: v,
              value: v,
            })) || []),
        ],
        showValueWhenEmpty: true,
        renderer: (incident) => {
          const isDisabled = disabled || incident.status === 'Resolved';
          const selectedAssigneeUserData = usersData?.find(
            (user) => user.email === incident.assignee,
          );

          return (
            <DropDown
              value={incident.assignee}
              menuButton={
                <Tooltip title="Set assignee">
                  <div>
                    <IconButtonStyled disabled={isDisabled}>
                      <Flex sx={{ alignItems: 'center', gap: 1 }}>
                        <UserIcon />
                        <SvgIcon
                          width={10}
                          strokeColor="gray4"
                          fillColor="gray4"
                        >
                          <ArrowDownIcon />
                        </SvgIcon>
                      </Flex>
                    </IconButtonStyled>
                  </div>
                </Tooltip>
              }
              showClear
              showSearch
              menuSelectedButton={
                <Tooltip title={incident.assignee}>
                  <IconButtonStyled disabled={isDisabled}>
                    <Flex sx={{ alignItems: 'center', gap: 1 }}>
                      {incident.assignee ? (
                        <Avatar
                          value={{
                            id:
                              selectedAssigneeUserData?.id ||
                              incident.assignee ||
                              '',
                            name:
                              selectedAssigneeUserData?.name ||
                              incident.assignee ||
                              '',
                          }}
                          size="small"
                        />
                      ) : (
                        <UserIcon />
                      )}
                      <SvgIcon width={10} strokeColor="gray4" fillColor="gray4">
                        <ArrowDownIcon />
                      </SvgIcon>
                    </Flex>
                  </IconButtonStyled>
                </Tooltip>
              }
              options={assigneeOptions || []}
              disabled={isDisabled}
              onChange={(val) => {
                onChange(incident.id, {
                  assignee:
                    assigneeOptions?.find((o) => o.value === val)?.value ||
                    NO_ASSIGNEE_VALUE,
                });
              }}
            />
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        width: 100,
        sortable: false,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: (incident) => {
          const isDisabled = disabled || incident.status === 'Resolved';
          return (
            <Menu
              tooltip="Set status"
              menuButton={
                <IconButtonStyled disabled={isDisabled}>
                  <Flex sx={{ alignItems: 'center', gap: 1 }}>
                    {smallStatusIconMap[incident.status]}
                    <SvgIcon width={10} strokeColor="gray4" fillColor="gray4">
                      <ArrowDownIcon />
                    </SvgIcon>
                  </Flex>
                </IconButtonStyled>
              }
              menuTitle="SET STATUS"
              options={statusOptions}
              closeOnChange
              onChange={(val) =>
                onChange(incident.id, {
                  status: val as IncidentDetails['status'],
                })
              }
            />
          );
        },
      },
      {
        id: 'actions',
        header: '',
        sortable: false,
        filterable: false,
        metaColumn: true,
        nonToggleable: true,
        width: 200,
        renderer: (incident) => (
          <Flex sx={{ justifyContent: 'flex-start', width: '100%' }}>
            <IncidentActionsColumnMenu
              incident={incident}
              isAiIncidentsEnabled={isAiIncidentsEnabled}
              severityOptions={severityOptions}
              statusOptions={statusOptions}
              assigneeOptions={assigneeOptions}
              disabled={disabled}
              onChange={onChange}
              onOpenAIChat={onOpenAIChat}
            />
          </Flex>
        ),
      },
    ],
    [
      severityOptions,
      statusOptions,
      assigneeOptions,
      usersData,
      disabled,
      incidentFilters,
      isAiIncidentsEnabled,
      onChange,
      onOpenAIChat,
    ],
  );

export default useIncidentColumns;
